/**
 * FolderTile + folder admin dialogs for the Documents page top-level grid.
 *
 * The Documents page's primary view is a grid of folder tiles. Each tile
 * is a self-contained card with a coloured icon block, primary name,
 * secondary metadata (count / "Empty"), and a kebab menu for folder-level
 * admin actions. Rename / delete dialogs live here too so FolderView
 * stays close to the 500-line cap.
 */

import { useEffect, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Folder,
  FolderPlus,
  MoreVertical,
  Pencil,
  Trash2,
  FileText,
  ChevronRight,
  Loader2,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { CategoryWithDocuments, DocumentCategory, documentsAPI } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { getCategoryColor, buildFolderOptions } from './utils';

interface FolderTileProps {
  category: CategoryWithDocuments;
  onOpen: () => void;
  onRename?: () => void;
  onDelete?: () => void;
  /** Create a subfolder nested under this folder. Only passed when the
   *  current user is permitted to nest folders (customer / staff / super
   *  admin) — see CanCreateSubfolder on the backend. */
  onCreateSubfolder?: () => void;
  /** Assign which suppliers may see this folder. Only passed for customer
   *  admins — the backend applies the same gate. */
  onManageAccess?: () => void;
  /** 'card' = self-contained tile (default). 'row' = compact row for the
   *  list-layout toggle, designed to sit inside a divide-y container. */
  variant?: 'card' | 'row';
}

/**
 * Polished top-level folder tile used by FolderView. Renders the folder
 * icon in a coloured block, the name + description, the document count
 * (or "Empty" hint), and a hover-revealed kebab menu when admin handlers
 * are provided. Supports a compact row variant for the list-layout toggle.
 */
export function FolderTile({ category, onOpen, onRename, onDelete, onCreateSubfolder, onManageAccess, variant = 'card' }: FolderTileProps) {
  const count = category.document_count;
  // Container folders hold no files of their own but nest populated
  // subfolders. Show their rolled-up shape ("3 folders · 331 documents")
  // instead of a misleading "Empty" — only a folder with no direct docs
  // AND no subfolders is truly empty.
  const subfolderCount = category.subfolder_count ?? 0;
  const totalDocs = category.total_document_count ?? count;
  const isEmpty = count === 0 && subfolderCount === 0;
  const isContainer = count === 0 && subfolderCount > 0;
  // Re-use the category-color palette as a solid soft icon block. The
  // CATEGORY_COLOR_MAP already covers dark mode.
  const colorClasses = getCategoryColor(category.color);
  const hasActions = Boolean(onRename || onDelete || onCreateSubfolder || onManageAccess);

  // Secondary metadata line — rendered identically in both variants.
  const metaContent = isEmpty ? (
    <span className="italic">Empty</span>
  ) : isContainer ? (
    <span className="inline-flex items-center gap-1.5">
      <Folder className="h-3.5 w-3.5" />
      <span className="tabular-nums">{subfolderCount}</span>
      <span>{subfolderCount === 1 ? 'folder' : 'folders'}</span>
      {totalDocs > 0 && (
        <>
          <span aria-hidden="true">·</span>
          <span className="tabular-nums">{totalDocs}</span>
          <span>{totalDocs === 1 ? 'document' : 'documents'}</span>
        </>
      )}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5">
      <FileText className="h-3.5 w-3.5" />
      <span className="tabular-nums">{count}</span>
      <span>{count === 1 ? 'document' : 'documents'}</span>
    </span>
  );

  // Shared menu body — rendered identically in the row and card variants.
  const menuItems = (
    <>
      {onCreateSubfolder && (
        <DropdownMenuItem onClick={onCreateSubfolder}>
          <FolderPlus className="h-4 w-4 mr-2" />New subfolder
        </DropdownMenuItem>
      )}
      {onRename && (
        <DropdownMenuItem onClick={onRename}>
          <Pencil className="h-4 w-4 mr-2" />Rename
        </DropdownMenuItem>
      )}
      {onManageAccess && (
        <DropdownMenuItem onClick={onManageAccess}>
          <Users className="h-4 w-4 mr-2" />Manage access
        </DropdownMenuItem>
      )}
      {(onCreateSubfolder || onRename || onManageAccess) && onDelete && <DropdownMenuSeparator />}
      {onDelete && (
        <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={onDelete}>
          <Trash2 className="h-4 w-4 mr-2" />Delete folder
        </DropdownMenuItem>
      )}
    </>
  );

  const actionsMenu = hasActions ? (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 transition-opacity"
          onClick={(e) => e.stopPropagation()}
          aria-label={`${category.name} actions`}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        {menuItems}
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null;

  // Compact row layout — single horizontal line, no Card wrapper. Used by
  // the List layout toggle. Sits inside a divide-y border container.
  if (variant === 'row') {
    return (
      <div
        onClick={onOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen();
          }
        }}
        className="group flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:bg-muted/50"
      >
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${colorClasses}`}
          aria-hidden="true"
        >
          <Folder className="h-4.5 w-4.5" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate" title={category.name}>
            {category.name}
          </div>
          {category.description && (
            <div className="text-xs text-muted-foreground truncate">
              {category.description}
            </div>
          )}
        </div>
        <div className="text-xs text-muted-foreground whitespace-nowrap">
          {metaContent}
        </div>
        {hasActions && <div className="-mr-2">{actionsMenu}</div>}
        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    );
  }

  return (
    <Card
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className="group relative cursor-pointer transition-all duration-150 hover:border-primary/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div
            className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${colorClasses}`}
            aria-hidden="true"
          >
            <Folder className="h-6 w-6" strokeWidth={1.75} />
          </div>
          {hasActions && (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 -mr-1 -mt-1 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 transition-opacity"
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`${category.name} actions`}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                {menuItems}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <div className="mt-3">
          <h3
            className="font-semibold text-base leading-tight line-clamp-2 break-words"
            title={category.name}
          >
            {category.name}
          </h3>
          {category.description && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
              {category.description}
            </p>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
          {metaContent}
          <span className="text-primary opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-0.5 font-medium">
            Open
            <ChevronRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

interface RenameFolderDialogProps {
  category: CategoryWithDocuments;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Rename a folder. Customer-only — only mounted when the caller has
 * permission to mutate folders. Surfaces backend validation errors via
 * sonner (e.g. duplicate folder name).
 */
export function RenameFolderDialog({ category, onClose, onSaved }: RenameFolderDialogProps) {
  const [name, setName] = useState(category.name);
  const [description, setDescription] = useState(category.description || '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !category.id) return;
    setSaving(true);
    try {
      await documentsAPI.updateCategory(category.id, {
        name: name.trim(),
        description: description.trim(),
      });
      toast.success(`Folder renamed to "${name.trim()}"`);
      onSaved();
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const msg = status === 403
        ? 'Only customer admins can rename folders.'
        : getApiErrorMessage(err, 'Failed to rename folder');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Rename folder</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="rename-folder-name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="rename-folder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rename-folder-desc">Description</Label>
            <Textarea
              id="rename-folder-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…
                </>
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface DeleteFolderDialogProps {
  category: CategoryWithDocuments;
  onClose: () => void;
  onDeleted: () => void;
}

/**
 * CR-5379: Confirm + execute folder deletion with explicit handling for
 * the documents inside. The customer chooses one of two modes:
 *
 *  - "Delete the folder AND all of its documents" (cascade)
 *  - "Delete the folder only — move documents to …" (reassign to
 *    another folder, or to Uncategorised when no target picked)
 *
 * Each per-document action is audit-logged on the backend so the
 * Documents > Audit History trail still reflects every move/delete.
 */
export function DeleteFolderDialog({ category, onClose, onDeleted }: DeleteFolderDialogProps) {
  const [deleting, setDeleting] = useState(false);
  const [mode, setMode] = useState<'reassign' | 'cascade'>('reassign');
  const [reassignTo, setReassignTo] = useState<string>(''); // '' = Uncategorised
  // Reassign-target picker only needs id/name, so a flat DocumentCategory
  // list is enough (and lets us reuse getAllCategories).
  const [folders, setFolders] = useState<DocumentCategory[]>([]);
  const count = category.document_count;
  const hasDocs = count > 0;

  useEffect(() => {
    if (!hasDocs) return;
    let cancelled = false;
    // getAllCategories walks every page — page_size:500 was silently capped
    // at the server max (100), so tenants with more folders lost reassign
    // targets beyond the first 100.
    documentsAPI.getAllCategories()
      .then(arr => {
        if (cancelled) return;
        // Exclude the folder being deleted — can't reassign into itself.
        setFolders(arr.filter(f => f.id !== category.id));
      })
      .catch(() => {/* picker stays minimal — Uncategorised always available */});
    return () => { cancelled = true; };
  }, [category.id, hasDocs]);

  const handleDelete = async () => {
    if (!category.id) return;
    setDeleting(true);
    try {
      const opts: { cascade?: boolean; reassignTo?: number | null } = hasDocs && mode === 'cascade'
        ? { cascade: true }
        : { reassignTo: reassignTo ? Number(reassignTo) : null };
      await documentsAPI.deleteCategory(category.id, opts);
      const suffix = !hasDocs
        ? ''
        : mode === 'cascade'
          ? ` and ${count} document${count === 1 ? '' : 's'}`
          : ` — ${count} document${count === 1 ? '' : 's'} moved to ${reassignTo ? folders.find(f => f.id === Number(reassignTo))?.name || 'selected folder' : 'Uncategorised'}`;
      toast.success(`Folder "${category.name}" deleted${suffix}`);
      onDeleted();
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const msg = status === 403
        ? 'Only customer admins can delete folders.'
        : getApiErrorMessage(err, 'Failed to delete folder');
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete folder?</DialogTitle>
          <DialogDescription>
            This will remove{' '}
            <span className="font-medium text-foreground">&quot;{category.name}&quot;</span>
            {hasDocs
              ? `. It contains ${count} document${count === 1 ? '' : 's'} — choose what happens to ${count === 1 ? 'it' : 'them'}.`
              : '. The folder is empty.'}
          </DialogDescription>
        </DialogHeader>

        {hasDocs && (
          <div className="space-y-3 py-2">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name="folder-delete-mode"
                value="reassign"
                checked={mode === 'reassign'}
                onChange={() => setMode('reassign')}
                className="mt-1"
              />
              <div className="flex-1 text-sm">
                <span className="font-medium">Delete folder only — move documents to…</span>
                <select
                  value={reassignTo}
                  onChange={(e) => setReassignTo(e.target.value)}
                  disabled={mode !== 'reassign'}
                  className="mt-1 w-full h-9 px-2 rounded-md border border-input bg-background text-sm disabled:opacity-50"
                >
                  <option value="">Uncategorised</option>
                  {buildFolderOptions(folders).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name="folder-delete-mode"
                value="cascade"
                checked={mode === 'cascade'}
                onChange={() => setMode('cascade')}
                className="mt-1"
              />
              <div className="flex-1 text-sm">
                <span className="font-medium text-destructive">Delete folder AND all its documents</span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  The {count} document{count === 1 ? '' : 's'} inside will be permanently deleted. Audit history is preserved.
                </p>
              </div>
            </label>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={deleting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting…
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />Delete folder
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface CreateSubfolderDialogProps {
  parent: CategoryWithDocuments;
  onClose: () => void;
  onCreated: () => void;
}

/**
 * Create a folder nested under `parent`. Only mounted when the current
 * user may nest folders (customer users, staff, or super admins — see
 * CanCreateSubfolder on the backend). Surfaces the backend's duplicate-name
 * and permission errors via sonner.
 */
export function CreateSubfolderDialog({ parent, onClose, onCreated }: CreateSubfolderDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !parent.id) return;
    setSaving(true);
    try {
      await documentsAPI.createCategory({
        name: name.trim(),
        description: description.trim(),
        parent: parent.id,
      });
      toast.success(`Subfolder "${name.trim()}" created in "${parent.name}"`);
      onCreated();
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const msg = status === 403
        ? 'Only customer users, staff, or super admins can create subfolders.'
        : getApiErrorMessage(err, 'Failed to create subfolder');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New subfolder</DialogTitle>
          <DialogDescription>
            Nested inside{' '}
            <span className="font-medium text-foreground">&quot;{parent.name}&quot;</span>.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="subfolder-name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="subfolder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. 2026 Audits"
              required
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="subfolder-desc">Description</Label>
            <Textarea
              id="subfolder-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating…
                </>
              ) : (
                'Create subfolder'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
